import { useEffect, useState } from "react";
import { HttpStatusCode } from "axios";

import { cleanTermContent } from "../../libs/term/term";

import {
  GetPublicTermsResponse,
  PublicTermPurpose,
  TermsVersion,
  getPublicTerms,
  getTermsById,
  getTermsVersion,
} from "../../services/etcServices";

import useFullScreenLoading from "../useFullScreenLoading";

import CommonSelect from "../../components/Select";
import HorizontalRule from "../../components/Rule/Horizontal";
import CommonButton from "../../components/Button";
import useSimplePopup from "../useSimplePopup";

type Props = {
  purpose: PublicTermPurpose;
  title?: React.ReactNode;
  isPopup?: boolean;
  onClose?: () => void;
};

export default function useTerms({
  purpose,
  title,
  isPopup = true,
  onClose,
}: Props): [{ terms?: GetPublicTermsResponse }, JSX.Element] {
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading, testId: "term" });

  const [termsVersionList, setTermsVersionList] = useState<TermsVersion[]>([]);
  const [selectedTerms, setSelectedTerms] = useState<TermsVersion>();
  const [terms, setTerms] = useState<GetPublicTermsResponse>();

  const needVersionPurpose: PublicTermPurpose[] = [
    "personal_process",
    "service",
    "location",
  ];

  const isRenderVersion = needVersionPurpose.includes(purpose);

  const compareVersions = (v1: string, v2: string): number => {
    const parts1 = v1.split(".").map(Number);
    const parts2 = v2.split(".").map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;

      if (num1 > num2) return -1;
      if (num2 > num1) return 1;
    }
    return 0;
  };

  // 최신버전이 가장 상위로 올 수 있도록 정렬
  const sortVersionList = (list: TermsVersion[]) => {
    return list.sort((a, b) => compareVersions(a.version, b.version));
  };

  const loadTermsVersionList = async (
    purpose: PublicTermPurpose
  ): Promise<TermsVersion[]> => {
    setIsLoading(true);
    let list: TermsVersion[] = [];

    try {
      const res = await getTermsVersion(purpose as PublicTermPurpose);
      if (res.status === HttpStatusCode.Ok) {
        list = sortVersionList(res.data);
        setTermsVersionList(list);
      }
    } catch (e: any) {
      const message =
        "약관 버전 목록을 불러오는데 실패했습니다.\n잠시 후 다시 시도해주세요.";

      if (e?.response?.data?.error) {
        open(e?.response?.data?.error);
      } else {
        open(message);
      }
    } finally {
      setIsLoading(false);
    }

    return list;
  };

  const loadTerms = async (termsId: number) => {
    setIsLoading(true);

    try {
      const res = await getTermsById(termsId);
      if (res.status === HttpStatusCode.Ok) {
        setTerms(res.data);
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e?.response?.data?.error);
      } else {
        open(
          "약관 내용을 불러오는데 실패했습니다.\n잠시 후 다시 시도해주세요."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTerms && selectedTerms.termsId) {
      loadTerms(selectedTerms.termsId);
    }
  }, [selectedTerms]);

  useEffect(() => {
    if (purpose) {
      if (isRenderVersion) {
        loadTermsVersionList(purpose as PublicTermPurpose).then((list) => {
          if (list.length > 0) {
            setSelectedTerms(list[0]);
          }
        });
      } else {
        getPublicTerms(purpose).then((res) => {
          if (res.status === HttpStatusCode.Ok) {
            setTerms(res.data);
          }
        });
      }
    }
  }, [purpose]);

  return [
    { terms },
    <>
      {loadingComponent}

      {isRenderVersion && (
        <div
          className="px-5 flex flex-col gap-2.5"
          data-testid="version-wrapper"
        >
          {!isPopup && (
            <div className="text-2xl font-medium">
              {title ?? terms?.categoryName}
            </div>
          )}
          {termsVersionList.length > 0 && (
            <>
              <div
                className="flex gap-2.5 items-center text-sm"
                data-testid="version-select-wrapper"
              >
                <CommonSelect
                  prefixIcon="Version"
                  defaultValue="버전 선택"
                  value={selectedTerms?.termsId.toString() ?? ""}
                  onChange={(option) => {
                    const selectedOption = termsVersionList.find(
                      (terms) => terms.termsId === Number(option.value)
                    );
                    setSelectedTerms(selectedOption);
                  }}
                  optionList={termsVersionList.map((terms) => ({
                    value: terms.termsId.toString(),
                    label: terms.version,
                  }))}
                  className={
                    "!border-brand-neutral-15 h-[30px] max-w-[170px] text-sm"
                  }
                  wrapperClassName="h-bottom-button max-w-[170px]"
                  optionClassName={"text-sm"}
                  optionOrder="between"
                  data-testid="version-select"
                />
              </div>
              <HorizontalRule margin="0" />
            </>
          )}
        </div>
      )}
      {terms?.content ? (
        <div
          dangerouslySetInnerHTML={{
            __html: cleanTermContent(terms?.content) || "&nbsp;",
          }}
          data-testid="terms-content"
        />
      ) : (
        <>
          <div className="p-5 pt-0 text-xl font-medium flex items-center justify-center h-[110px]">
            약관을 찾을 수 없습니다.
          </div>
          {isPopup && (
            <div className="w-full flex">
              <CommonButton
                variant="primary"
                className=" rounded-none w-full h-bottom-button"
                onClick={() => {
                  onClose?.();
                }}
              >
                확인
              </CommonButton>
            </div>
          )}
        </>
      )}

      {simplePopupComponent}
    </>,
  ];
}
